import { LoadIntoDocument } from "../../Utils/LoadIntoDocument";

declare global {
    interface Window {
        lastInputElement?: HTMLInputElement;
        kontur?: {
            drive?: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                upload?: any;
                init: (callback: (error?: Error) => void) => void;
            };
        };
    }
}

export interface CustomFile {
    name: string;
    size: number;
    contentUri?: string;
}

export interface DriveTask {
    cancel: () => void;
    then: (resolve: () => void, reject: () => void) => undefined;
}

interface DriveResult {
    initDrive: Promise<void>;
    getDriveTask: () => DriveTask;
}

export type UploadToDrive = (file: CustomFile, changeProgress: (done: number, total: number) => void) => DriveResult;

let fileInput: HTMLInputElement | undefined;

export function selectUserFiles(multiple: boolean): Promise<CustomFile[]> {
    return new Promise(resolve => {
        const input = getFileInput();
        const savedOnFocus = document.body.onfocus;

        // no other way to detect closure of open file window
        document.body.onfocus = function handleFocus(this: GlobalEventHandlers, ...args): void {
            const result = savedOnFocus ? savedOnFocus.call(this, ...args) : null;

            if (!input.files) {
                resolve([]);
            }

            return result;
        };

        input.multiple = multiple;
        input.onchange = () =>
            onFileInputChange(input, files => {
                document.body.onfocus = savedOnFocus;
                resolve(Array.prototype.slice.call(files, 0));
            });
        input.click();
    });
}

export function uploadToDrive(file: CustomFile, onProgress: (done: number, total: number) => void): DriveResult {
    let initDrive = Promise.resolve();

    if (!window.kontur || !window.kontur.drive || !window.kontur.drive.upload) {
        initDrive = new Promise<void>((resolve, reject) => {
            const driveClientJsUri = `https://api.testkontur.ru/drive/v1/js/client/js`;

            LoadIntoDocument.script(driveClientJsUri, error => {
                if (window.kontur == undefined || window.kontur.drive == undefined) {
                    reject(new Error("Drive client is not loaded"));
                    return;
                }
                if (error) {
                    reject(error);
                } else {
                    window.kontur.drive.init(err => {
                        if (err) {
                            reject(err);
                        } else {
                            resolve();
                        }
                    });
                }
            });
        });
    }

    return {
        initDrive: initDrive,
        getDriveTask: () => {
            if (window.kontur == undefined || window.kontur.drive == undefined) {
                throw new Error("Drive client is not loaded");
            }
            const driveTask = window.kontur.drive.upload(file, {
                contentType: "application/xml;charset=utf-8",
                daysToLive: 30,
                srv: "candyFront",
            });

            if (driveTask.progress) {
                driveTask.progress(onProgress);
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return driveTask;
        },
    };
}

function getFileInput(): HTMLInputElement {
    if (fileInput == undefined) {
        const input = document.createElement("input");

        input.type = "file";
        input.style.display = "none";

        // IE doesn't show open dialog if the input isn't in document.
        document.body.appendChild(input);
        fileInput = input;
    }

    return fileInput;
}

function onFileInputChange(input: HTMLInputElement, callback: (files: null | FileList) => void): void {
    if (fileInput != undefined && fileInput.parentNode != undefined) {
        fileInput.parentNode.removeChild(fileInput);
        fileInput.onchange = null;
        fileInput = undefined;
    }
    callback(input.files);
}
